import type { HandbookChapter } from '../model';

/** Was neben dem Ticket zur täglichen Arbeit gehört. */

const zeit: HandbookChapter = {
  id: 'zeiterfassung',
  title: 'Zeiterfassung und Auswertung',
  summary: 'Zeit am Ticket erfassen, Timer benutzen, Zeiten auswerten und ausleiten.',
  audience: 'agent',
  route: '/app/time-report',
  sections: [
    {
      id: 'erfassen',
      title: 'Zeit am Ticket erfassen',
      blocks: [
        {
          kind: 'shot',
          file: 'zeiterfassung',
          caption: 'Der Reiter „Erfasste Zeiten“ am Ticket.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Am Ticket den Reiter „Erfasste Zeiten“ öffnen.' },
            {
              text: 'Dauer und Tätigkeit eintragen.',
              detail:
                'Die Dauer nimmt, was Ihnen gerade einfällt: „30“, „1:30“ oder „1,5h“. Unter dem Feld steht, wie das System es verstanden hat — prüfen Sie diese Zeile, bevor Sie speichern. Der Tag wird auf den heutigen gesetzt; ein Nachtrag für gestern wird deshalb heute gezählt.',
            },
            {
              text: 'Kreuz bei „Abrechenbar“ prüfen.',
              detail:
                'Die Vorbelegung kommt aus der gewählten Tätigkeitsart und zieht mit, wenn Sie eine andere wählen. Was das Kreuz zeigt, wird gespeichert — für Tätigkeiten, die nicht abgerechnet werden, wählen Sie es ab. Verrechnet wird nichts; dieses System stellt keine Rechnungen, das Kennzeichen ist ein Nachweis.',
            },
            { text: 'Notiz dazuschreiben, wenn die Tätigkeitsart allein nicht genügt.' },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Nachträglich ändern',
          text: 'Ein erfasster Eintrag lässt sich über „Bearbeiten“ korrigieren — die Dauer und das Kreuz bei „Abrechenbar“. Nehmen Sie diesen Weg und nicht Löschen und neu Anlegen: Ein geänderter Eintrag trägt seine Vorgeschichte im Protokoll, ein neu angelegter sieht aus, als hätte es den ersten nie gegeben.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Timer',
          text: '„Timer starten“ läuft mit, während Sie arbeiten. „Timer anhalten“ trägt die gelaufene Zeit in das Dauerfeld ein und legt noch nichts an — erfasst wird erst mit „Zeit erfassen“. Genau dazwischen korrigieren Sie die Dauer: Ein Timer, der über die Mittagspause lief, ist der Regelfall und kein Fehler.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Fremde Zeiten',
          text: 'Sie erfassen und ändern Ihre eigenen Zeiten. Für den Kollegen, der vom Einsatz kommt, nachtragen darf nur, wer das Recht time.manage trägt — in der Regel die Administration.',
        },
      ],
    },
    {
      id: 'auswerten',
      title: 'Die Zeitauswertung',
      blocks: [
        {
          kind: 'shot',
          file: 'zeitauswertung',
          caption: 'Zeitauswertung, gruppiert nach Kunde.',
        },
        {
          kind: 'text',
          text: 'Die Zeitauswertung beantwortet: Wie viel Zeit wofür. Gruppieren lässt sie sich nach Kunde, Bearbeiter, Tätigkeit, Tag oder Ticket. Der Zeitraum wird über „Von“ und „Bis“ gesetzt, das Kreuz „Nur abrechenbare“ blendet den Rest aus.',
        },
        {
          kind: 'text',
          text: 'Das Diagramm zeigt die größten zehn Gruppen; alle Zeilen stehen in der Tabelle darunter. „Als CSV herunterladen“ liefert den vollständigen Auszug — für die Weiterverarbeitung außerhalb des Systems.',
        },
        { kind: 'route', to: '/app/time-report', label: 'Zur Zeitauswertung' },
      ],
    },
  ],
};

const termine: HandbookChapter = {
  id: 'termine',
  title: 'Termine',
  summary:
    'Termine am Ticket eintragen, Kollegen dazunehmen, Einladung in den eigenen Kalender, Startseite und Terminübersicht.',
  audience: 'agent',
  route: '/app/calendar',
  sections: [
    {
      id: 'eintragen',
      title: 'Einen Termin am Ticket eintragen',
      blocks: [
        {
          kind: 'shot',
          file: 'termine-ticket',
          caption:
            'Der Reiter „Termine“ am Ticket mit einem eingetragenen Termin und der Maske für den nächsten.',
        },
        {
          kind: 'text',
          text: 'Ein Termin gehört immer zu einem Ticket — der Vor-Ort-Einsatz, der Rückruf, der Austausch im Serverraum. An einem Ticket können beliebig viele Termine stehen; jeder hat einen oder mehrere Mitarbeiter.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Am Ticket den Reiter „Termine“ öffnen und „Termin eintragen“ wählen.' },
            {
              text: 'Datum, Beginn und Ende eintragen.',
              detail:
                'Vorgeschlagen ist die nächste volle Stunde für eine Stunde. Die Uhrzeiten gelten in deutscher Zeit, auch wenn Ihr Rechner gerade anders eingestellt ist. Ein Termin in der Maske beginnt und endet am selben Tag; das Ende muss nach dem Beginn liegen, sonst bleibt „Speichern“ gesperrt.',
            },
            {
              text: 'Ort eintragen, wenn es einen gibt.',
              detail:
                'Freier Text: „beim Kunden, 2. OG“, „Telefon“, „Teams“. Das Feld ist wahlfrei.',
            },
            {
              text: 'In „Beschreibung“ kurz festhalten, was bei dem Termin geschehen soll.',
            },
            {
              text: 'Mitarbeiter auswählen.',
              detail:
                'Sie selbst sind vorbelegt. Weitere Kollegen nehmen Sie über das Auswahlfeld dazu, mit dem × an ihrem Namen wieder heraus. Zur Auswahl stehen nur aktive Mitarbeiter dieses Mandanten — Ansprechpartner des Kunden lassen sich nicht eintragen.',
            },
            {
              text: 'Bei Bedarf den Haken „Termineinladung per E-Mail an alle Mitarbeiter senden“ setzen und speichern.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Kunden sehen keine Termine',
          text: 'Ein Termin ist eine Absprache im Haus. Er erscheint nicht im Kundenportal, auch nicht am eigenen Ticket des Kunden. Soll der Kunde vom Termin erfahren, schreiben Sie ihm eine Antwort im Verlauf.',
        },
        { kind: 'route', to: '/app/tickets', label: 'Zu den Tickets' },
      ],
    },
    {
      id: 'einladung',
      title: 'Die Einladung in den Kalender',
      blocks: [
        {
          kind: 'text',
          text: 'Mit dem Haken bekommt jeder eingetragene Mitarbeiter eine E-Mail an die Adresse aus seinem Profil. Sie enthält eine Kalendereinladung: In Outlook erscheint sie wie jede andere Besprechungsanfrage, und ein Klick auf „Annehmen“ trägt den Termin ein. Andere Kalenderprogramme öffnen die angehängte Datei „einladung.ics“.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Was drinsteht',
              text: 'Zeit, Ort, Ticketnummer und Betreff, der Kunde, die Terminbeschreibung, die Beschreibung des Tickets und ein Link zurück ins Ticket. Der Termin soll auch ohne die Anwendung verständlich sein — beim Kunden vor der Tür oder unterwegs ohne Netz.',
            },
            {
              term: 'Wohin sie geht',
              text: 'Nur an die Adressen aus den Profilen der eingetragenen Mitarbeiter. Eine freie Adresse lässt sich nicht angeben.',
            },
            {
              term: 'Wann sie ankommt',
              text: 'Mit dem nächsten Versandlauf, in der Regel innerhalb einer Minute. Ist der Mailserver nicht erreichbar, versucht das System es bis zu fünfmal mit wachsendem Abstand.',
            },
            {
              term: '„eingeladen“ am Namen',
              text: 'Zeigt, wer die Einladung bekommen hat. Ohne diesen Zusatz steht der Termin nur in der Anwendung, nicht im Kalender des Mitarbeiters.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Kundendaten verlassen die Anwendung',
          text: 'Die Einladung trägt Kundennamen und Ticketinhalt in das Postfach und den Kalender des Mitarbeiters — und damit zu dem Anbieter, bei dem dieses Postfach liegt, etwa Microsoft 365. Setzen Sie den Haken deshalb bewusst. Das ist so gewollt; ohne Haken bleibt der Termin in der Anwendung.',
        },
      ],
    },
    {
      id: 'aendern',
      title: 'Ändern, Kollegen herausnehmen, löschen',
      blocks: [
        {
          kind: 'text',
          text: 'Über „Bearbeiten“ ändern Sie Zeit, Ort, Beschreibung und die Mitarbeiter. Wer schon eine Einladung hat, bleibt auf dem Laufenden — das System schickt ohne weiteres Zutun:',
        },
        {
          kind: 'table',
          head: ['Sie ändern …', 'Wer schon eingeladen ist, bekommt …'],
          rows: [
            [
              'Zeit, Ort oder Beschreibung',
              'die neue Fassung; Outlook verschiebt den Termin im Kalender',
            ],
            [
              'Nur die Mitarbeiter, ein Kollege kommt dazu',
              'nichts — nur der neue Kollege eine Einladung, wenn der Haken gesetzt ist',
            ],
            ['Einen Kollegen heraus', 'nur er eine Absage; Outlook entfernt den Termin bei ihm'],
            ['Löschen', 'alle eine Absage'],
            ['Den Haken nachträglich setzen', 'alle, die noch keine haben, eine Einladung'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Haken wieder entfernen',
          text: 'Wer schon eingeladen ist, bekommt Änderungen und Absagen auch nach dem Entfernen des Hakens. Sonst stünde bei ihm ein Termin im Kalender, den es so nicht mehr gibt. Der Haken regelt nur, ob neue Einladungen hinausgehen.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Zusagen in Outlook',
          text: 'Die Einladung bittet nicht um Antwort. Schickt jemand trotzdem eine Zusage, verwirft das System sie beim Mailabruf — aus ihr entsteht kein Ticket und kein Beitrag.',
        },
      ],
    },
    {
      id: 'uebersicht',
      title: 'Startseite und Terminübersicht',
      blocks: [
        {
          kind: 'shot',
          file: 'termine-startseite',
          caption: 'Die Startseite mit „Heutige Termine“ und „Termine für morgen“.',
        },
        {
          kind: 'text',
          text: 'Auf der Startseite stehen unter „Meine Arbeit“ zwei Spalten: „Heutige Termine“ und „Termine für morgen“. Gezeigt werden nur Termine, bei denen Sie eingetragen sind, mit Uhrzeit, Ticket, Kunde und Ort. Ein Klick auf das Ticket öffnet es.',
        },
        {
          kind: 'shot',
          file: 'termine-kalender',
          caption: 'Die Terminübersicht in der Wochenansicht.',
        },
        {
          kind: 'text',
          text: 'Unter „Termine“ in der Navigation links steht Ihre Terminübersicht. „Woche“ zeigt jeden Tag von Montag bis Sonntag mit allen Terminen; „Monat“ zeigt das Monatsraster mit bis zu drei Terminen je Tag — „+2 weitere“ springt in die Woche dieses Tages. „Zurück“, „Heute“ und „Weiter“ blättern. Ansicht und Zeitraum stehen in der Adresszeile: Die Zurück-Taste des Browsers führt in die vorige Woche, und eine Ansicht lässt sich als Lesezeichen ablegen.',
        },
        {
          kind: 'shot',
          file: 'termine-monat',
          caption: 'Die Terminübersicht in der Monatsansicht.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Termine über Mitternacht',
          text: 'Ein Termin von 23 bis 1 Uhr steht an beiden Tagen — auf der Startseite wie in der Übersicht. Er betrifft ja beide.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Nur Ihre eigenen Termine',
          text: 'Die Übersicht zeigt die Termine, bei denen Sie eingetragen sind, nicht die der Kollegen. Die Termine eines Tickets sehen alle, die das Ticket sehen, im Reiter „Termine“.',
        },
        { kind: 'route', to: '/app/calendar', label: 'Zur Terminübersicht' },
      ],
    },
  ],
};

const crm: HandbookChapter = {
  id: 'crm',
  title: 'Kunden und Ansprechpartner',
  summary:
    'Kundenakte, Standorte, Ansprechpartner, E-Mail-Domänen, Verträge, Kommunikationshistorie, Import.',
  audience: 'agent',
  route: '/app/crm',
  sections: [
    {
      id: 'liste',
      title: 'Die Kundenliste',
      blocks: [
        {
          kind: 'shot',
          file: 'crm-liste',
          caption: 'Die Kundenliste mit Suche und VIP-Filter.',
        },
        {
          kind: 'text',
          text: 'Gesucht wird über Name und Kundennummer. Der Filter „Nur VIP“ zeigt die Kunden, die bevorzugt behandelt werden — an jedem ihrer Tickets erscheint dazu ein Hinweis.',
        },
        {
          kind: 'text',
          text: 'Wie in der Ticketliste öffnet ein Klick irgendwo in die Zeile die Kundenakte, der Tabellenkopf bleibt beim Rollen stehen, und der Umschalter „Normal/Eng“ verringert den Zeilenabstand. Die Zeilenhöhe gilt für alle Listen der Anwendung.',
        },
      ],
    },
    {
      id: 'akte',
      title: 'Die Kundenakte',
      blocks: [
        {
          kind: 'shot',
          file: 'crm-akte',
          caption: 'Die Kundenakte mit ihren Bereichen.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Überblick',
              text: 'Offene und abgeschlossene Tickets, laufende Verträge, die zuletzt geänderten Tickets.',
            },
            {
              term: 'Ansprechpartner',
              text: 'Wer beim Kunden anruft. Die E-Mail-Adresse ist je Mandant eindeutig — die Zuordnung eingehender Mails baut darauf auf. Für Absender, die hier nicht stehen, greifen die E-Mail-Domänen des Kunden.',
            },
            { term: 'Standorte', text: 'Anschriften. Einer davon ist der Hauptstandort.' },
            {
              term: 'Verträge',
              text: 'Serviceverträge als Stammdaten: Nummer, Art, Laufzeit, vereinbarte Stunden. Als Nachweis, nicht als Abrechnungsgrundlage.',
            },
            {
              term: 'Kommunikationshistorie',
              text: 'Beiträge an Tickets und Aktivitäten am Kunden, zusammengeführt in einem Strang. Interne Notizen erscheinen hier nicht.',
            },
            {
              term: 'Aktivitäten',
              text: 'Was außerhalb eines Tickets geschehen ist — ein Telefonat, ein Termin, ein Gespräch auf dem Flur. Der Zeitpunkt ist der des Geschehens, nicht der der Eintragung.',
            },
            {
              term: 'Wissensmanagement',
              text: 'Die Wissensartikel zu diesem Kunden — und die allgemein gültigen, denn die gelten hier ebenfalls. „In der Wissensdatenbank öffnen“ führt in die volle Ansicht, vorgefiltert auf den Kunden; ein dort angelegter Artikel ist ihm gleich zugeordnet.',
            },
          ],
        },
      ],
    },
    {
      id: 'mailversand',
      title: 'Automatische E-Mails an einen Kunden abstellen',
      blocks: [
        {
          kind: 'text',
          text: 'Manche Kunden wollen keine automatische Post: ein Haus mit eigenem Servicedesk, das seine Vorgänge bei Ihnen führt, aber nicht bei jeder Anfrage eine Mail bekommen möchte. Der Haken „Benachrichtigungen per E-Mail“ steht im Kundendialog — beim Anlegen und beim Bearbeiten.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Die Kundenakte öffnen und „Bearbeiten“ klicken.' },
            {
              text: 'Den Haken „Benachrichtigungen per E-Mail“ entfernen.',
              detail:
                'Bei einem neuen Kunden ist er gesetzt. Das ist Absicht: Wer nichts sagt, bekommt seine Post — eine ausbleibende Mail meldet sich nicht, eine überflüssige schon.',
            },
            { text: 'Speichern.' },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Was der Haken stoppt — und was nicht',
          text: 'Ohne ihn bleiben die Eingangsbestätigung und die Zufriedenheitsumfrage aus. Die Antwort, die Sie am Ticket selbst schreiben und abschicken, geht weiterhin hinaus — sonst schriebe ein Bearbeiter ins Leere, ohne es zu merken. Nicht betroffen sind außerdem die Benachrichtigungen an Ihre eigenen Bearbeiter und die geplanten Berichte: Die gehen ins eigene Haus.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Zwei verschiedene Schalter',
          text: 'Am Ansprechpartner gibt es den Widerspruch nach Art. 21 DSGVO — das ist ein Recht einer Person. Der Haken am Kunden ist etwas anderes: eine Abrede mit der Organisation. In der Kundenakte erscheint dann neben dem Namen das Schild „Keine automatischen E-Mails“. Es genügt, dass einer von beiden greift.',
        },
      ],
    },
    {
      id: 'maildomaenen',
      title: 'E-Mail-Domänen: wenn nicht jeder Absender bekannt ist',
      blocks: [
        {
          kind: 'text',
          text: 'Eingehende Mails ordnet das System über die Absenderadresse zu: Steht sie bei einem Ansprechpartner, entsteht das Ticket bei dessen Kunden. Nur schreibt selten immer dieselbe Person. Geführt ist verwaltung@muster-gmbh.de, geschrieben hat einkauf@muster-gmbh.de — und die Mail landet unter „Anfragen“, obwohl niemand im Zweifel ist, zu wem sie gehört.',
        },
        {
          kind: 'text',
          text: 'Dagegen stehen die E-Mail-Domänen im Kundendialog. Was dort eingetragen ist, gilt als „gehört diesem Kunden“: Trifft die Absenderadresse keinen Ansprechpartner, entscheidet die Domäne. Eine Domäne je Zeile, ohne @ — „muster-gmbh.de“.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Die Kundenakte öffnen und „Bearbeiten“ klicken.' },
            {
              text: 'Unter „E-Mail-Domänen“ die Domänen des Kunden eintragen, eine je Zeile.',
              detail:
                'Ein führendes @, eine ganze Adresse oder ein https:// davor stören nicht — das Feld räumt beim Speichern auf. Hat der Kunde nach einer Umbenennung eine zweite Domäne, kommt sie in die nächste Zeile.',
            },
            { text: 'Speichern. In der Kundenakte stehen die Domänen danach in der Seitenspalte.' },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Der Absender wird zum Ansprechpartner',
          text: 'Greift die Domäne, legt das System den Absender als Ansprechpartner beim Kunden an — mit dem Schild „automatisch angelegt“. Das Schild sagt: Diesen Eintrag hat niemand geprüft. Er ist nicht auftragsberechtigt; das bleibt Ihre Entscheidung. Sobald Sie den Eintrag einmal bearbeiten, verschwindet das Schild.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Keine Freemail-Domänen',
          text: 'gmx.de, web.de, gmail.com und ihresgleichen weist das System ab. Eine solche Domäne einem Kunden zuzuordnen hieße, ihm die Post aller Nutzer dieses Anbieters zuzuschreiben — und der Fehler meldet sich nicht, er zeigt sich als Vorgang beim falschen Kunden.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Eine Domäne gehört einem Kunden',
          text: 'Trägt jemand dieselbe Domäne bei einem zweiten Kunden ein, wird das abgewiesen — mit dem Namen des Kunden, bei dem sie schon steht. Wechselt eine Domäne den Besitzer, entfernen Sie sie erst beim alten Kunden und tragen sie dann beim neuen ein.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Der Ansprechpartner geht vor',
          text: 'Steht eine Adresse bei einem Ansprechpartner, gilt dieser — auch wenn die Domäne auf einen anderen Kunden zeigt. Die genauere Angabe schlägt die allgemeinere. Trifft weder das eine noch das andere zu, bleibt es beim bisherigen Weg: Die Mail wartet unter „Anfragen“ auf einen Menschen.',
        },
      ],
    },
    {
      id: 'ansprechpartner',
      title: 'Ansprechpartner anlegen',
      blocks: [
        {
          kind: 'steps',
          steps: [
            { text: 'In der Kundenakte den Bereich „Ansprechpartner“ öffnen.' },
            { text: '„Neuer Ansprechpartner“ klicken.' },
            {
              text: 'Name und E-Mail-Adresse eintragen.',
              detail:
                'Über diese Adresse ordnet das System eingehende Mails dem Kunden zu. Ein Tippfehler heißt: Das Ticket landet ohne Kundenbezug im Eingang.',
            },
            {
              text: '„Auftragsberechtigt“ ankreuzen, wenn die Person Anfragen im Namen des Kunden stellen darf.',
            },
            {
              text: '„Hauptansprechpartner“ setzen, wenn es die erste Anlaufstelle ist.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Widerspruch gegen Kontaktaufnahme',
          text: 'Am Ansprechpartner lässt sich vermerken, dass er Umfragen und Benachrichtigungen widersprochen hat (Art. 21 DSGVO). Das System hört dann auf, ihn anzuschreiben — ohne dass Sie daran denken müssen.',
        },
      ],
    },
    {
      id: 'import',
      title: 'Kunden und Kontakte importieren',
      blocks: [
        {
          kind: 'shot',
          file: 'crm-import',
          caption: 'Der Import: erst Vorschau, dann Übernahme.',
        },
        {
          kind: 'steps',
          steps: [
            {
              text: 'Auf „Import“ wechseln und wählen, ob Kunden oder Kontakte importiert werden.',
            },
            {
              text: 'CSV-Datei mit Kopfzeile wählen.',
              detail:
                'Kunden: name (Pflicht), customer_number, status, industry, vat_id, website, notes. Kontakte: customer_number und last_name (Pflicht), first_name, email, phone, mobile, position.',
            },
            {
              text: '„Vorschau“ klicken.',
              detail:
                'Sie sehen je Zeile, ob sie angelegt, aktualisiert oder übersprungen wird — und woran es liegt.',
            },
            {
              text: 'Erst danach „Übernehmen“.',
              detail:
                'Die Übernahme steht bewusst erst nach der Vorschau zur Verfügung. Ein Import mit verrutschter Spalte ist mühsam zurückzunehmen.',
            },
          ],
        },
        { kind: 'route', to: '/app/crm/import', label: 'Zum Import' },
      ],
    },
    {
      id: 'loeschen',
      title: 'Kunden abschalten statt löschen',
      blocks: [
        {
          kind: 'note',
          tone: 'warning',
          title: 'Ein Kunde mit Tickets bleibt bestehen',
          text: 'Das Löschen wird abgelehnt, solange Tickets oder Tochtergesellschaften daran hängen. Wer einen Kunden nicht mehr betreut, setzt ihn auf „Inaktiv“. Der Bestand bleibt auswertbar, und die Aufbewahrungsfristen laufen wie vorgesehen ab.',
        },
      ],
    },
  ],
};

const wissen: HandbookChapter = {
  id: 'wissensdatenbank',
  title: 'Wissensdatenbank',
  summary: 'Artikel schreiben, prüfen lassen, freigeben — und was der Kunde davon sieht.',
  audience: 'agent',
  route: '/app/knowledge',
  sections: [
    {
      id: 'suchen',
      title: 'Suchen und lesen',
      blocks: [
        {
          kind: 'shot',
          file: 'wissensdatenbank',
          caption: 'Trefferliste links, Artikel rechts.',
        },
        {
          kind: 'text',
          text: 'Die Suche läuft beim Tippen, anders als die Ticketsuche. Ein Wissensbestand hat Hunderte Einträge, keine Hunderttausende, und wer eine Lösung sucht, weiß selten das genaue Wort. Die Suche geht über Wortstämme — „gedruckt“ findet „Drucker“.',
        },
        {
          kind: 'text',
          text: 'Am Ticket erscheinen passende Artikel als „Lösungsvorschläge“, ohne dass Sie suchen müssen. Steht die Antwort schon da, sparen Sie sich die Arbeit.',
        },
        {
          kind: 'text',
          text: 'Die Liste zeigt zunächst 25 Artikel. Gibt es mehr, steht darunter „Mehr laden“ mit der Zahl der bereits geladenen. Suche und Filter bleiben dabei stehen — nachgeladen wird immer innerhalb dessen, was Sie eingegrenzt haben.',
        },
        {
          kind: 'text',
          text: 'Jeder gesetzte Filter steht als abwählbare Marke über der Liste: „Suche: Drucker“, „Kategorie: Netzwerk“, „Stand: Entwurf“. Ein Klick darauf nimmt genau diese Einschränkung zurück, „Filter zurücksetzen“ alle auf einmal. Der Grund ist die leere Trefferliste: Ohne den Hinweis, was gerade eingegrenzt ist, sieht sie aus, als wäre der Artikel gelöscht worden.',
        },
        {
          kind: 'text',
          text: 'Die Trefferliste lässt sich mit den Pfeiltasten durchgehen. Der Artikel rechts wechselt mit — wer dreißig Treffer vergleicht, greift dafür nicht dreißigmal zur Maus. Die ganze Trefferkarte ist anklickbar, nicht nur der Titel.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Lesen ist der Normalzustand',
          text: 'Ein Artikel steht zunächst da, wie ihn der Kollege später liest — mit Überschriften, Listen und Links, nicht als HTML im Textfeld. Wer ihn ändern will, klickt „Bearbeiten“. Vorher bekam jeder, der schreiben darf, ausschließlich das Formular zu sehen und hat seinen eigenen Artikel nie gelesen.',
        },
      ],
    },
    {
      id: 'schreiben',
      title: 'Einen Artikel schreiben',
      blocks: [
        {
          kind: 'text',
          text: 'Der Artikelbereich hat zwei Spalten: links der Text, rechts die Eigenschaften — Kategorie, Sichtbarkeit, „Gilt für“, Tags, Prüfdatum. Beim Lesen stehen sie als Angaben da, beim Bearbeiten als Felder, in derselben Reihenfolge an derselben Stelle. Der Kopf mit Titel, Stand und „Speichern“ bleibt beim Rollen stehen.',
        },
        {
          kind: 'steps',
          steps: [
            {
              text: '„Neuer Artikel“ klicken — oder am gelösten Ticket „Artikel aus Ticket“.',
              detail:
                'Der zweite Weg ist der bessere: Was einmal gelöst wurde, ist beim Schreiben noch frisch.',
            },
            {
              text: 'Titel erfassen, dann die Abschnitte füllen.',
              detail:
                'Der Kernsatz aus sechs Abschnitten steht als Vorgabe da. Leere bleiben stehen und erscheinen in der Leseansicht nicht. In jedem Abschnitt steht derselbe Editor wie am Ticket: Fett, Kursiv, Überschriften, Aufzählungen, Zitat, Code, Link und Tabelle. Was Sie schreiben, sehen Sie beim Schreiben — kein HTML im Textfeld.',
            },
            { text: 'Kategorie wählen.' },
            {
              text: 'Tags setzen, wenn es welche gibt.',
              detail:
                'Im Feld „Tags“ tippen und wählen; gewählte Tags stehen als Marken darüber. Es sind dieselben Tags wie am Ticket — kein eigener Vorrat. Nur so führt „Exchange“ am Vorgang auch zu den Artikeln darüber.',
            },
            {
              text: 'Sichtbarkeit setzen.',
              detail:
                'Sie greift erst nach der Freigabe. Bis dahin ist der Artikel ein Entwurf und für niemanden außer Bearbeitern sichtbar.',
            },
            {
              text: 'Unter „Gilt für“ entscheiden, ob der Artikel für alle Kunden gilt.',
              detail:
                'Nichts auswählen heißt „gültig für alle“ — das ist die Vorgabe und der häufigere Fall. Wer einen oder mehrere Kunden auswählt, schränkt ein: im Feld „Kunde hinzufügen“ tippen und wählen, für jeden Kunden einmal. Die Gewählten stehen als Marken darüber; ein Klick auf eine Marke nimmt den Kunden wieder heraus.',
            },
            {
              text: 'Datum der nächsten Prüfung setzen.',
              detail:
                'Ohne Datum erinnert nichts. Ein Artikel über eine Anlage, die es nicht mehr gibt, fällt sonst erst dem auf, der danach handelt.',
            },
            {
              text: 'Speichern — mit dem Knopf im Kopf oder mit Strg+S beziehungsweise ⌘+S.',
              detail:
                'Bei einer Änderung an einem bestehenden Artikel gehört der Änderungsvermerk dazu: Er steht später in der Liste der Fassungen und beantwortet die Frage, warum es eine zweite gibt.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Ungespeichertes geht nicht verloren',
          text: 'Solange etwas offen ist, steht „Nicht gespeichert“ im Kopf. Ein Klick auf einen anderen Treffer, „Neuer Artikel“ oder das Schließen des Reiters fragt vorher nach. Vorher genügte ein Fehlklick, um zehn Minuten Arbeit loszuwerden — lautlos.',
        },
      ],
    },
    {
      id: 'anhaenge',
      title: 'Dateien am Artikel',
      blocks: [
        {
          kind: 'text',
          text: 'Ein Artikel trägt Dateien wie ein Ticket: Schaltplan, Vertragsauszug, Bildschirmfoto der Maske, die gemeint ist. Sie stehen im Reiter „Anhänge“ neben „Inhalt“ und „Fassungen“, sobald der Artikel gespeichert ist — vorher gibt es nichts, woran sie hängen könnten. Die Zahl am Reiter sagt, wie viele es sind; eine Datei lässt sich auf die gestrichelte Fläche ziehen oder über einen Klick darauf auswählen.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Immer Herunterladen, nie Anzeigen',
          text: 'Eine Datei wird ausgeliefert und nicht im Browser geöffnet. Das ist Absicht: Eine hochgeladene HTML-Datei liefe sonst im Ursprung der Anwendung, und damit wäre jede Sitzung angreifbar.',
        },
        {
          kind: 'text',
          text: 'Wer den Artikel nicht sehen darf, kommt auch an seine Dateien nicht — auch nicht über einen kopierten Link. Für einen internen Artikel und für einen, der einem anderen Kunden zugeordnet ist, gilt das gleichermaßen.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Anhänge gehören zur Fassung',
          text: 'Eine Datei hängt nicht am Artikel, sondern an der Fassung, in der sie hinzugefügt wurde. Eine neue Fassung übernimmt die Dateien der vorherigen. Ein Anhang wird deshalb nicht gelöscht, sondern über eine neue Fassung entfernt — die alte Fassung behält ihn. Damit bleibt beantwortbar, was zu einem Zeitpunkt am Artikel hing; genau dafür werden die Fassungen getrennt geführt.',
        },
      ],
    },
    {
      id: 'gilt-fuer',
      title: 'Für wen ein Artikel gilt',
      blocks: [
        {
          kind: 'text',
          text: 'Die meisten Anleitungen gelten für jeden Kunden — „Kennwort zurücksetzen“, „VPN einrichten“. Manche nicht: Wie die Telefonanlage eines bestimmten Kunden zurückgesetzt wird, geht die übrigen nichts an. Dafür gibt es im Artikel das Feld „Gilt für“.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Nichts ausgewählt',
              text: 'Gültig für alle Kunden. Die Vorgabe.',
            },
            {
              term: 'Ein oder mehrere Kunden ausgewählt',
              text: 'Der Artikel gehört zu diesen Kunden. Mehrere sind ausdrücklich möglich — ein Wissensstück betrifft oft eine Gruppe.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Die Zuordnung ist eine Grenze, nicht nur Ordnung',
          text: 'Steht ein zugeordneter Artikel auf Sichtbarkeit „Portal“, dann sehen ihn im Portal ausschließlich die genannten Kunden. Ein Portalnutzer eines anderen Kunden findet ihn weder in der Suche noch über einen Verweis. Die Bearbeitung sieht dagegen immer alles — sie betreut alle Kunden.',
        },
        {
          kind: 'text',
          text: 'Die Zuordnung lässt sich jederzeit ändern. Alle Kunden wieder abzuwählen macht den Artikel erneut allgemein gültig.',
        },
        {
          kind: 'text',
          text: 'Ausgewählt wird im Feld „Kunde hinzufügen“: tippen, wählen, für jeden Kunden einmal. Was gewählt ist, steht darüber als Marke und lässt sich dort mit einem Klick wieder entfernen. Die frühere Mehrfachauswahl hatte einen tückischen Fehlgriff: Ein Klick ohne Steuerungstaste löschte die gesamte Zuordnung, ohne dass etwas darauf hinwies.',
        },
      ],
    },
    {
      id: 'abschnitte',
      title: 'Der Aufbau eines Artikels',
      blocks: [
        {
          kind: 'text',
          text: 'Ein Artikel wird in Abschnitten erfasst und nicht als ein langer Text. Jeder Abschnitt hat einen Titel und ein eigenes Feld. In der Leseansicht stehen sie alle offen untereinander — zuklappen lässt sich jeder einzeln, was abgearbeitet ist, darf aus dem Weg. Ab drei gefüllten Abschnitten steht darüber ein Verzeichnis, das unmittelbar zu „Durchführung“ springt.',
        },
        {
          kind: 'text',
          text: 'Der Grund ist das Suchen, nicht das Schreiben: Wer eine Anleitung überfliegt, sucht „Durchführung“ und nicht den dritten Absatz von oben. Ein Bestand, in dem jeder Artikel anders aufgebaut ist, muss vollständig gelesen werden, um brauchbar zu sein. Aufgeklappt wird nichts: Wer eine Anleitung am Telefon abarbeitet, braucht sie vollständig vor sich — jeder Klick ist eine Hand, die vom Hörer muss.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Zweck / Problem — worum es geht und was schiefgeht.' },
            { text: 'Betrifft — System, Kunde, Gerät.' },
            { text: 'Voraussetzungen — Zugänge, Rechte, Werkzeuge.' },
            { text: 'Durchführung — die Schritte selbst.' },
            { text: 'Fehlerbehebung / Hinweise — was erfahrungsgemäß hakt.' },
            { text: 'Verweise & Anhänge — Links, verwandte Artikel, Dateien.' },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Leere Abschnitte sind erlaubt',
          text: 'Nicht jeder Artikel braucht alle sechs. Ein Abschnitt ohne Inhalt bleibt in der Maske stehen, erscheint in der Leseansicht aber nicht. Zusätzliche Abschnitte lassen sich hinzufügen und verschieben; die Reihenfolge des Kernsatzes bleibt.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Alte Artikel bleiben, wie sie sind',
          text: 'Artikel aus der Zeit vor den Abschnitten — und die importierten aus dem Altsystem — haben keine Gliederung. Sie werden als ein Abschnitt „Inhalt“ angezeigt und in der Maske weiter als ein Textfeld bearbeitet. Wer einen davon gliedern will, klickt „In Abschnitte gliedern“; ohne diesen Klick wird kein Text zerschnitten.',
        },
      ],
    },
    {
      id: 'freigabe',
      title: 'Der Freigabelauf',
      blocks: [
        {
          kind: 'terms',
          items: [
            { term: 'Entwurf', text: 'In Arbeit. Sieht niemand außer Bearbeitern.' },
            { term: 'Zur Prüfung gegeben', text: 'Wartet auf jemanden mit dem Freigaberecht.' },
            {
              term: 'Freigegeben',
              text: 'Sichtbar entsprechend der gesetzten Sichtbarkeit — intern, im Portal oder öffentlich.',
            },
            { term: 'Zurückgezogen', text: 'War freigegeben, gilt nicht mehr.' },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Freigeben darf nicht jeder',
          text: 'Schreiben und zur Prüfung geben kann jeder Bearbeiter. Freigeben und die Sichtbarkeit bis „öffentlich“ setzen verlangt das Recht knowledge.publish. Der Grund liegt auf der Hand: „Öffentlich“ heißt, dass der Text ohne Anmeldung im Netz steht.',
        },
        {
          kind: 'text',
          text: 'Jede Freigabe erzeugt eine neue Fassung. Ältere Fassungen bleiben einsehbar — mit dem Vermerk, was sich geändert hat.',
        },
        {
          kind: 'text',
          text: 'Im Reiter „Fassungen“ steht die Kette von der neuesten nach unten: Nummer, Änderungsvermerk und Zeitpunkt. Ein Klick auf eine Zeile klappt sie auf und zeigt, **was** in dieser Fassung stand — in denselben Abschnitten wie die Leseansicht, dazu die Dateien, die damals daran hingen. Die geltende Fassung ist als „Geltend“ gekennzeichnet; bei jeder anderen steht oben ein Hinweis, dass Sie eine historische Fassung lesen. Es steht immer eine Fassung offen; ein Klick auf eine andere wechselt dorthin.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Alte Fassungen lassen sich nicht bearbeiten',
          text: 'Es gibt kein „Zurücksetzen“ aus der Historie. Eine Fassung ist der Nachweis, was zu einem Zeitpunkt im Artikel stand — ließe sie sich ändern, wäre sie keiner mehr. Wer einen alten Stand wiederhaben will, kopiert ihn in die Bearbeitung und speichert ihn als neue Fassung; damit bleibt sichtbar, dass zurückgegangen wurde, und warum.',
        },
      ],
    },
    {
      id: 'pflege',
      title: 'Den Bestand pflegen',
      blocks: [
        {
          kind: 'text',
          text: '„An fällige Prüfungen erinnern“ legt ein Ticket für alle Artikel an, deren Prüfdatum erreicht ist — eines für alle, nicht eines je Artikel. Der Lauf lässt sich von Hand anstoßen oder über einen Zeitplan automatisieren.',
        },
        {
          kind: 'text',
          text: '„Kategorien pflegen“ öffnet einen Dialog: anlegen, verschachteln, löschen. Er steht im Kopf der Wissensdatenbank und nicht mehr dauerhaft zwischen Filter und Trefferliste — Kategorien pflegt man einmal im Quartal, gesucht wird täglich.',
        },
        {
          kind: 'text',
          text: 'Unter dem Artikel steht „War dieser Artikel hilfreich?“ als Sterne. Der Durchschnitt und die Zahl der Bewertungen stehen daneben — eine Note ohne die Zahl dahinter sagt nichts.',
        },
        { kind: 'route', to: '/app/knowledge', label: 'Zur Wissensdatenbank' },
      ],
    },
  ],
};

const berichte: HandbookChapter = {
  id: 'berichte',
  title: 'Startseite und Berichte',
  summary: 'Welche Zahlen es gibt, was sie zählen und wo sie herkommen.',
  audience: 'agent',
  route: '/app/reports',
  sections: [
    {
      id: 'startseite',
      title: 'Die Startseite',
      blocks: [
        { kind: 'shot', file: 'startseite', caption: 'Die Startseite mit Kacheln und Diagrammen.' },
        {
          kind: 'text',
          text: 'Die Startseite zeigt, was gerade anliegt. Über „Kacheln wählen“ stellen Sie ein, was Sie sehen wollen. Wer nichts einstellt, sieht alle; wer alle abwählt, sieht wieder alle — eine leere Startseite ist keine.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Offene Tickets',
              text: 'Alles ohne die abgeschlossenen — Neu, In Bearbeitung und beide Wartezustände. Nicht zu verwechseln mit der Statusgruppe „In Bearbeitung“, die enger ist.',
            },
            { term: 'Frist überschritten', text: 'Lösungsfrist gerissen.' },
            { term: 'Gestern eingegangen', text: 'Das Aufkommen des Vortags.' },
            { term: 'Zeit diese Woche', text: 'Was in dieser Woche erfasst wurde.' },
            {
              term: 'Verteilung nach Zustand',
              text: 'Wo die offenen Tickets im Augenblick stehen.',
            },
            {
              term: 'Aufkommen im Verlauf',
              text: 'Neu angelegte Tickets je Tag, dreißig Tage zurück. Tage ohne Ticket zählen als null — eine Lücke in der Linie sähe aus wie ein fehlender Messwert.',
            },
            {
              term: 'SLA-Einhaltung',
              text: 'Anteil der Tickets der letzten dreißig Tage, deren Lösungsfrist gehalten wurde, je Kunde. Gezählt werden nur Tickets mit einer Frist.',
            },
            { term: 'Auslastung', text: 'Erfasste Zeit je Bearbeiter, dreißig Tage zurück.' },
            {
              term: 'Meine Arbeit',
              text: 'Die fünf offenen Tickets auf Ihrem Namen mit der knappsten Frist — offen im Sinne von „noch zu tun“, ein frisch zugewiesenes Ticket steht also dabei. Sortiert nach der Frist und nicht nach dem Datum: Das älteste Ticket ist nicht das dringendste. Tickets ohne Frist stehen am Ende; sie sind nicht die dringendsten, sondern die ohne Uhr. Der Verweis darunter führt in die vollständige Liste mit derselben Einschränkung.',
            },
          ],
        },
        {
          kind: 'text',
          text: 'Kacheln, zu denen es eine Liste gibt, sind anklickbar und führen in genau die Tickets, die sie zählen: „Offene Tickets“ in die mit „Offen“ gefilterte Liste, „Zeit diese Woche“ in die Zeitauswertung. „Genau die“ ist wörtlich gemeint — die Liste hinter einer Kachel muss so viele Zeilen haben, wie die Zahl darauf sagt. Ohne diesen Weg wüsste man, dass zwölf Tickets offen sind, und müsste sie danach von Hand suchen.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Die Richtungsangabe unter einer Zahl',
          text: 'Unter „Gestern eingegangen“ steht der Vergleich der letzten sieben Tage mit den sieben davor. Er erscheint nur, wenn er sich berechnen lässt — liegen weniger als vierzehn Tage vor oder war die Vorwoche leer, steht dort nichts. Eine Null, die wie „unverändert“ aussieht, wäre eine Aussage, die die Zahlen nicht hergeben. Unterschiede unter einem Prozent gelten als unverändert; alles darunter ist Rauschen und kein Trend.',
        },
      ],
    },
    {
      id: 'berichte',
      title: 'Die Berichte',
      blocks: [
        {
          kind: 'shot',
          file: 'berichte',
          caption: 'Bericht wählen, Gruppierung wählen, ausleiten.',
        },
        {
          kind: 'table',
          head: ['Bericht', 'Was er zählt'],
          rows: [
            ['Ticketaufkommen', 'Wie viele Tickets entstanden sind.'],
            [
              'Bearbeitungsdauer',
              'Mittlere Zeit bis zur Lösung — in verstrichener Zeit, nicht in Geschäftszeit. Für die Zusage zählt die SLA-Uhr; hier geht es um die Wahrnehmung des Kunden, und der wartet auch am Wochenende.',
            ],
            [
              'SLA-Einhaltung',
              'Anteil gehaltener Lösungsfristen. Nur Tickets mit Frist — ein Ticket ohne Zusage kann sie weder halten noch reißen.',
            ],
            ['Auslastung', 'Erfasste Zeit je Bearbeiter, Kunde oder Tätigkeit.'],
            ['Kundenzufriedenheit', 'Mittelwert der Umfragen nach Ticketabschluss.'],
          ],
        },
        {
          kind: 'text',
          text: 'Jeder Bericht lässt sich gruppieren und als CSV oder PDF ausleiten. Das Diagramm zeigt die größten zehn Werte; alle Zeilen stehen in der Tabelle darunter.',
        },
        { kind: 'route', to: '/app/reports', label: 'Zu den Berichten' },
      ],
    },
    {
      id: 'versand',
      title: 'Berichte regelmäßig verschicken',
      blocks: [
        {
          kind: 'text',
          text: 'Unter den Berichten steht der Versand: Welcher Bericht, wann, über welchen Zeitraum und an wen. Der übliche Fall ist die Wochenübersicht, die montags früh im Postfach der Geschäftsführung liegt.',
        },
        {
          kind: 'steps',
          steps: [
            { text: '„Versand einrichten“ klicken und den Bericht wählen.' },
            {
              text: 'Zeitpunkt als Cron-Ausdruck eintragen — „0 8 * * 1“ heißt montags um acht.',
              detail: 'Gilt in der Zeitzone des Mandanten.',
            },
            {
              text: 'Zeitraum in Tagen setzen.',
              detail:
                'Relativ, vom Lauf aus gerechnet. Ein fester Zeitraum ergäbe jede Woche dieselben Zahlen.',
            },
            {
              text: 'Empfänger wählen — Bearbeiter und Ansprechpartner stehen zur Auswahl.',
              detail:
                'Eine Adresse, die nirgends geführt wird, tippen Sie ein und übernehmen sie mit der Eingabetaste. Was beim Klick auf „Anlegen“ noch im Feld steht, zählt mit.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Jeder bekommt seine eigene Nachricht',
          text: 'Ein Verteiler im To-Feld verriete jedem Empfänger, wer sonst noch mitliest — bei einer Auswertung über die Arbeit der Bearbeiter ist das keine Kleinigkeit. Der Bericht steht im Rumpf der Mail und nicht im Anhang: Ein Anhang wird seltener geöffnet, und eine Zahl, die niemand ansieht, ändert nichts.',
        },
      ],
    },
  ],
};

export const arbeitChapters: HandbookChapter[] = [zeit, termine, crm, wissen, berichte];
